import axios from "axios";
import {
  COMPLETE_TASK_SUBMIT,
  CREATE_NEW_FORM,
  CREATE_NEW_QUESTION,
  DELETE_QUESTIONS,
  GET_MY_FORM,
  GET_MY_FORMS,
  LOCK_MY_FORM,
  LOGIN_USER_URL,
  LOGOUT_USER_URL,
  PUBLISH_MY_FORM,
  REFRESH_USER_URL,
  SUBMIT_FORM,
  UNLOCK_MY_FORM,
  UNPUBLISH_MY_FORM,
  UPDATE_ANSWER_OPTION_QUESTION,
  UPDATE_FORM_NEW,
  UPDATE_QUESTION,
  UPDATE_USER_NOTIFICATIONS,
} from "../constants/apiUrls";
import { localStorageKeys, urlRoutes } from "../constants/page";
import { jwtDecode } from "jwt-decode";
import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  setTokens,
} from "../utils/authStorage";
import { authClient } from "./http";

let refreshPromise = null;
export const headers = {};
export const formErrorResponse = "There was an error generating your form.";

export function apiErrorText(err) {
  const data = err?.response?.data;
  if (typeof data === "string" && data.trim()) return data.trim();
  if (typeof data?.message === "string" && data.message.trim()) {
    return data.message.trim();
  }
  return null;
}

export function serverMessage(response, fallback = null) {
  if (typeof response === "string") {
    const text = response.trim();
    if (text && text !== formErrorResponse && text !== "Success") return text;
  }
  if (typeof response?.message === "string" && response.message.trim()) {
    return response.message.trim();
  }
  return fallback;
}

export function apiFailed(response) {
  if (response === formErrorResponse) return true;
  return (
    typeof response === "string" &&
    response.trim() !== "" &&
    response !== "Success"
  );
}

function writeFailure(err) {
  console.error(err);
  return apiErrorText(err) || formErrorResponse;
}

function readFailure(err) {
  console.error(err);
  return apiErrorText(err);
}

export const loginUser = async (userData) => {
  try {
    const response = await axios.post(LOGIN_USER_URL, userData, { headers });

    if (response?.data?.accessToken) {
      sessionStorage.removeItem(localStorageKeys.impersonationCompany);
      setTokens({
        accessToken: response.data.accessToken,
        refreshToken: response.data.refreshToken,
      });
      return response.data.user;
    }

    return response;
  } catch (e) {
    console.error({ e });
    return e;
  }
};

export const handleLogout = async ({ redirect = true } = {}) => {
  const refreshToken = getRefreshToken();

  try {
    if (refreshToken) {
      await axios.post(LOGOUT_USER_URL, { refreshToken });
    }
  } catch {
    // still clear locally
  } finally {
    clearTokens();
    if (redirect && window.location.pathname !== urlRoutes.login) {
      window.location.assign(urlRoutes.login);
    }
  }
};

export const renewTokens = async () => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      throw new Error("No refresh token");
    }

    const { data } = await axios.post(REFRESH_USER_URL, {
      refreshToken,
      accessToken: getAccessToken(),
    });

    if (!data?.accessToken || !data?.refreshToken) {
      throw new Error("Invalid refresh response");
    }

    setTokens({
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
    });

    return data;
  })();

  try {
    return await refreshPromise;
  } catch (error) {
    const status = error?.response?.status;
    if (status === 401 || status === 403) {
      await handleLogout();
    }
    throw error;
  } finally {
    refreshPromise = null;
  }
};

export const apiGet = async (url) => {
  try {
    const response = await axios.get(url);
    return response.data;
  } catch (e) {
    return readFailure(e);
  }
};

export const apiGetAuth = async (url) => {
  try {
    const response = await authClient.get(url);
    return response.data ?? response;
  } catch (e) {
    return readFailure(e);
  }
};

export const apiPost = async (url, body) => {
  try {
    const response = await axios.post(url, body);
    return response.data;
  } catch (err) {
    return writeFailure(err);
  }
};

export const apiPostAuth = async (url, body) => {
  try {
    const response = await authClient.post(url, body);
    return response.data;
  } catch (err) {
    return writeFailure(err);
  }
};

export const apiPatchAuth = async (url, body) => {
  try {
    const response = await authClient.patch(url, body);
    return response.data;
  } catch (err) {
    return writeFailure(err);
  }
};

export const apiDeleteAuth = async (url) => {
  try {
    const response = await authClient.delete(url);
    return response.data;
  } catch (err) {
    return writeFailure(err);
  }
};

export const apiGetForms = async () => {
  try {
    const response = await authClient.get(GET_MY_FORMS);
    return response.data;
  } catch (e) {
    return readFailure(e);
  }
};

export const apiGetForm = async (formID) => {
  try {
    const response = await authClient.get(`${GET_MY_FORM}/${formID}`);
    return response.data;
  } catch (e) {
    return readFailure(e);
  }
};

export const apiUpdateForm = async (form) => {
  try {
    const response = await authClient.put(`${UPDATE_FORM_NEW}`, form);
    return response?.data;
  } catch (err) {
    return writeFailure(err);
  }
};

export const apiGenerateNewForm = async (formBody) => {
  try {
    const jwt = localStorage.getItem(localStorageKeys.accessToken);
    const response = await axios.post(CREATE_NEW_FORM, formBody, {
      headers: {
        authorization: `bearer ${jwt}`,
      },
    });
    return response.data;
  } catch (err) {
    return writeFailure(err);
  }
};

export const getAttachmentById = async (fileId, url, callback) => {
  const jwt = localStorage.getItem(localStorageKeys.accessToken);
  const endpoint = `${url}/${fileId}`;
  try {
    const result = await fetch(endpoint, {
      method: "GET",
      headers: {
        authorization: `bearer ${jwt}`,
        Accept: "application/json",
      },
    });

    let data = null;
    try {
      data = await result.json();
    } catch {
      data = null;
    }

    if (!result.ok || !data || typeof data !== "object" || !data.fileName) {
      if (typeof data === "string" && data.trim()) return data.trim();
      return result.ok ? null : "Could not open this file.";
    }

    callback(data);
    return null;
  } catch (error) {
    console.error(error);
    return "Could not open this file.";
  }
};

export const updateAnswerOptionQuestion = async (question, questionsArr) => {
  try {
    const jwt = localStorage.getItem(localStorageKeys.accessToken);
    const response = await axios.post(
      UPDATE_ANSWER_OPTION_QUESTION,
      { question, questions: questionsArr },
      {
        headers: {
          authorization: `bearer ${jwt}`,
        },
      },
    );
    return response;
  } catch (err) {
    return writeFailure(err);
  }
};

export const apiSubmitForm = async (formSubmit) => {
  try {
    const response = await authClient.post(SUBMIT_FORM, formSubmit);
    return response.data;
  } catch (err) {
    return writeFailure(err);
  }
};

export const saveQuestion = async (question) => {
  try {
    const jwt = localStorage.getItem(localStorageKeys.accessToken);
    const response = await axios.post(CREATE_NEW_QUESTION, question, {
      headers: {
        authorization: `bearer ${jwt}`,
      },
    });
    return response.data;
  } catch (err) {
    return writeFailure(err);
  }
};

export const updateQuestion = async (question) => {
  try {
    const jwt = localStorage.getItem(localStorageKeys.accessToken);
    const response = await axios.post(UPDATE_QUESTION, question, {
      headers: {
        authorization: `bearer ${jwt}`,
      },
    });
    return response.data;
  } catch (err) {
    return writeFailure(err);
  }
};

export const apiSubmitCompletedTask = async (taskEdit) => {
  try {
    const jwt = localStorage.getItem(localStorageKeys.accessToken);
    const response = await axios.post(COMPLETE_TASK_SUBMIT, taskEdit, {
      headers: {
        "Content-Type": "multipart/form-data",
        authorization: `bearer ${jwt}`,
      },
    });
    return response.data;
  } catch (err) {
    return writeFailure(err);
  }
};

export const apiPublishForm = async (id) => {
  try {
    const jwt = localStorage.getItem(localStorageKeys.accessToken);
    const response = await axios.get(`${PUBLISH_MY_FORM}/${id}`, {
      headers: {
        authorization: `bearer ${jwt}`,
      },
    });
    return response.data;
  } catch (err) {
    return writeFailure(err);
  }
};

export const apiUnpublishForm = async (id) => {
  try {
    const jwt = localStorage.getItem(localStorageKeys.accessToken);
    const response = await axios.get(`${UNPUBLISH_MY_FORM}/${id}`, {
      headers: {
        authorization: `bearer ${jwt}`,
      },
    });
    return response.data;
  } catch (err) {
    return writeFailure(err);
  }
};

export const apiLockForm = async (id) => {
  try {
    const jwt = localStorage.getItem(localStorageKeys.accessToken);
    const response = await axios.get(`${LOCK_MY_FORM}/${id}`, {
      headers: {
        authorization: `bearer ${jwt}`,
      },
    });
    return response.data;
  } catch (err) {
    return writeFailure(err);
  }
};

export const apiUnlockForm = async (id) => {
  try {
    const jwt = localStorage.getItem(localStorageKeys.accessToken);
    const response = await axios.get(`${UNLOCK_MY_FORM}/${id}`, {
      headers: {
        authorization: `bearer ${jwt}`,
      },
    });
    return response.data;
  } catch (err) {
    return writeFailure(err);
  }
};

export const apiDeleteQuestion = async (questionID, answerID) => {
  try {
    const jwt = localStorage.getItem(localStorageKeys.accessToken);
    const response = await axios.delete(
      `${DELETE_QUESTIONS}/${questionID}/${answerID}`,
      {
        headers: {
          authorization: `bearer ${jwt}`,
        },
      },
    );
    return response;
  } catch (err) {
    return writeFailure(err);
  }
};

export const apiPostUserQANotifications = async (formID, formBody) => {
  try {
    const jwt = localStorage.getItem(localStorageKeys.accessToken);
    const response = await axios.post(
      `${UPDATE_USER_NOTIFICATIONS}/${formID}`,
      formBody,
      {
        headers: {
          authorization: `bearer ${jwt}`,
        },
      },
    );
    return response.data;
  } catch (err) {
    return writeFailure(err);
  }
};
